import { $axios } from '@/boot/initApi'
// import { Notification } from '@/models' // eslint-disable-line
export default () => {
  // if (!Notification.axios) Notification.axios = $axios
  // const {
  //   isSuccess: notificationSuccess, // eslint-disable-line
  //   isPending: notificationPending, // eslint-disable-line
  //   refetch: notificationRefetch // eslint-disable-line
  // } = Notification.index()

  return {
    /**
     * Fetch the user from the server
     */
    fetchUser: async function () {
      try {
        const { data } = await $axios.get('/api/user')
        if (data && data.id) {
          this.storeUser(data)
        }
      } catch (err) {
        console.error(err)
        // Maintenance 503 is not an auth failure — the session is still valid.
        // Mark as authenticated so the maintenance screen shows instead of
        // bouncing the user to the login page. Full profile loads after recovery.
        if (err.response?.status === 503) {
          this.isAuthenticated = true
        } else {
          this.$reset()
        }
      }
    },

    /**
     * Get the user from store or fetch it from the server.
     *
     * @returns {Object|false} The user object or false if the user is not logged in.
     */
    getUser: async function () {
      return this.isAuthenticated ? this.$state : false
    },

    /**
     * Store the user in the store
     *
     * @param {Object} data
     */
    storeUser: async function (data) {
      this.isAuthenticated = true
      await this.$patch(data)
    },

    checkAuthStatus: async function () {
      // console.log('%ccheckAuthStatus', 'color:lime')
      if (this.id) {
        try {
          const { data } = await $axios.get('/user', { validateStatus: s => s === 200 || s === 401 })
          if (data && data.id) {
            this.storeUser(data)
            return true
          } else {
            this.$reset()
            return false
          }
        } catch (err) {
          console.error(err)
          return false
        }
      }
    },

    /**
     * Logout the user
     */
    logout: async function () {
      try {
        await $axios.post('/logout')
        this.$reset()
        this.router.push({ name: 'Login' })
      } catch (err) {
        console.error(err)
      }
    },

    /**
     * Update the authenticated user's profile fields (name/surname/email/phoneNumber).
     * Returns the raw response so the caller can react to `emailVerificationRequired`.
     */
    updateProfile: async function (payload) {
      const { data } = await $axios.patch('/api/user', payload)
      await this.storeUser(data.user)
      return data
    },

    /**
     * Start TOTP enrollment. Returns `{secret, otpauthUrl}` — render
     * otpauthUrl as a QR code and show secret as the manual-entry fallback.
     * Not yet enabled until confirmMfaTotp succeeds.
     */
    enrollMfaTotp: async function () {
      const { data } = await $axios.post('/api/mfa/totp')
      return data
    },

    /**
     * Confirm enrollment with the first real code from the user's
     * authenticator app. Returns `{message, recoveryCodes}` — recoveryCodes
     * is only present (shown once, never retrievable again) when this is
     * the user's first confirmed MFA method account-wide; `null` otherwise.
     */
    confirmMfaTotp: async function ({ code }) {
      const { data } = await $axios.post('/api/mfa/totp/confirm', { code })
      this.mfaMethods = [...new Set([...this.mfaMethods, 'totp'])]
      return data
    },

    /**
     * Disable TOTP MFA for the current account.
     */
    disableMfaTotp: async function () {
      await $axios.delete('/api/mfa/totp')
      this.mfaMethods = this.mfaMethods.filter(method => method !== 'totp')
    },

    /**
     * Enable email MFA, instantly (no confirm step, unlike TOTP). Returns
     * `{message, recoveryCodes}` — recoveryCodes is only present when this
     * is the user's first confirmed MFA method account-wide; `null` otherwise.
     */
    enrollMfaEmail: async function () {
      const { data } = await $axios.post('/api/mfa/email')
      this.mfaMethods = [...new Set([...this.mfaMethods, 'email'])]
      return data
    },

    /**
     * Disable email MFA for the current account.
     */
    disableMfaEmail: async function () {
      await $axios.delete('/api/mfa/email')
      this.mfaMethods = this.mfaMethods.filter(method => method !== 'email')
    },

    /**
     * Toggle the info panel and persist state to localStorage + server.
     */
    toggleInfoPanel: function (value) {
      const updated = { ...(this.settings ?? {}), infoPanelIsActive: value }
      this.settings = updated
      this._syncSettings(updated)
    },

    /**
     * Set the explore view mode ('list' | 'grid') and persist to localStorage + server.
     */
    setExploreView: function (value) {
      const updated = { ...(this.settings ?? {}), exploreView: value }
      this.settings = updated
      this._syncSettings(updated)
    },

    /**
     * Generic single-key settings update with server sync. Used by composables
     * that manage their own preference keys (e.g. table column prefs).
     */
    updateSetting: function (key, value) {
      const updated = { ...(this.settings ?? {}), [key]: value }
      this.settings = updated
      this._syncSettings(updated)
    },

    /**
     * Debounced settings sync to server. Written separately so it can be replaced in tests.
     */
    _syncSettings: (function () {
      let timer = null
      return function (settings) {
        clearTimeout(timer)
        timer = setTimeout(async () => {
          try {
            await $axios.patch('/api/user/settings', { settings })
          } catch (err) {
            console.error('Failed to sync user settings', err)
          }
        }, 1000)
      }
    }())
  }
}
